import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// iter150AZ.33 — Frontend caching layer. Wraps the app in a
// QueryClientProvider so heavy fetches (customer detail,
// quote-detail, compressors, parts-purchased, service-forecast,
// service-history) can share cached responses across navigations.
//
// Defaults:
//   * staleTime    : 30s   — most data updates lazily, so a 30-second
//                            window of "consider this fresh" is a
//                            reasonable trade-off between snappiness
//                            and freshness.
//   * gcTime       : 5 min — keep cached responses warm for re-visits
//                            within a single working session.
//   * retry        : 1     — don't pile up retries on 4xx/5xx; one
//                            polite retry is plenty.
//   * refetchOnWindowFocus: false — too noisy for a back-office app
//                            where users alt-tab to phones / emails
//                            constantly.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// iter120 — Eagerly register the offline-aware service worker on app
// boot. Previously this only ran when a user opted into push, which
// meant the runtime cache for customer detail pages, the Warranty
// Policy PDF, and the Help Center never primed for users who skipped
// the push prompt. Register is idempotent (browsers dedupe by URL),
// so calling it here AND inside `usePush` is safe.
if ("serviceWorker" in navigator && process.env.NODE_ENV !== "test") {
  // Defer until after first paint so SW install doesn't compete with
  // critical-path resources.
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {
      /* fail silently — feature degrades gracefully */
    });
  });

  // When a new SW takes control (after a redeploy), reload exactly
  // once so the page picks up the freshly cached static bundles.
  // Without this, users on stale bundles can sit on the old UI
  // because the previously installed SW already served the old
  // ``bundle.js`` from cache before the new SW had a chance to
  // claim the client. Guarded by ``__sw_reloaded__`` to prevent
  // refresh loops.
  let __sw_reloaded__ = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (__sw_reloaded__) return;
    __sw_reloaded__ = true;
    window.location.reload();
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
